import type { ApiClient } from '@momonos/api-client'
import { Badge, Button, Card, Loader, Modal, useToast } from '@momonos/ui'
import { useCallback, useEffect, useState } from 'react'

/** FR-INV-1 / FR-INV-5 — stock levels and adjustments. */
interface StockRow {
  ingredientId: string
  name: string
  unit: string
  stock: number
  lowStockThreshold: number
  isLow: boolean
}

export function InventoryScreen({ api, branchId }: { api: ApiClient; branchId: string | null }) {
  const { show } = useToast()
  const [stock, setStock] = useState<StockRow[] | null>(null)
  const [adjusting, setAdjusting] = useState<StockRow | null>(null)
  const [quantity, setQuantity] = useState('')
  const [reason, setReason] = useState('')
  const [type, setType] = useState<'purchase' | 'waste' | 'adjustment'>('purchase')
  const [isSaving, setSaving] = useState(false)

  const load = useCallback(async () => {
    const result = await api.get<{ stock: StockRow[] }>('/v1/inventory', {
      query: { branch: branchId ?? undefined },
    })
    setStock(result.stock)
  }, [api, branchId])

  useEffect(() => {
    void load().catch(() => show('Could not load inventory', { tone: 'error' }))
  }, [load, show])

  async function submit() {
    if (!adjusting) return

    const amount = Number(quantity)
    if (!Number.isFinite(amount) || amount === 0) {
      show('Enter an amount', { tone: 'warning' })
      return
    }

    setSaving(true)
    try {
      await api.post(
        '/v1/inventory/adjust',
        {
          ingredientId: adjusting.ingredientId,
          // Waste and downward corrections are entered as a positive number and
          // signed here, so a manager never has to type a minus sign.
          quantity: type === 'purchase' ? Math.abs(amount) : -Math.abs(amount),
          type,
          reason,
        },
        { query: { branch: branchId ?? undefined } },
      )

      show(`${adjusting.name} updated`, { tone: 'success' })
      setAdjusting(null)
      setQuantity('')
      setReason('')
      await load()
    } catch (caught) {
      show((caught as Error).message || 'Could not adjust stock', { tone: 'error' })
    } finally {
      setSaving(false)
    }
  }

  if (!stock) return <Loader label="Loading inventory" />

  return (
    <div className="space-y-4">
      <h1 className="font-bold text-2xl text-content">Inventory</h1>

      <div className="grid gap-3">
        {stock.map((row) => (
          <Card key={row.ingredientId} className="flex items-center gap-4">
            <div className="flex-1">
              <p className="font-medium text-content">{row.name}</p>
              <p className="mt-1 flex items-center gap-2 text-content-secondary">
                <span className="tabular">
                  {row.stock} {row.unit}
                </span>
                {row.isLow && <Badge tone="yellow">Low</Badge>}
                {row.stock <= 0 && <Badge tone="red">Out</Badge>}
              </p>
            </div>

            <Button variant="secondary" onClick={() => setAdjusting(row)}>
              Adjust
            </Button>
          </Card>
        ))}
      </div>

      <Modal
        isOpen={adjusting !== null}
        onClose={() => setAdjusting(null)}
        title={adjusting ? `Adjust ${adjusting.name}` : ''}
        footer={
          <>
            <Button variant="ghost" onClick={() => setAdjusting(null)}>
              Cancel
            </Button>
            <Button isLoading={isSaving} onClick={submit}>
              Save
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <div className="flex gap-2">
            {(['purchase', 'waste', 'adjustment'] as const).map((option) => (
              <Button
                key={option}
                variant={type === option ? 'primary' : 'secondary'}
                onClick={() => setType(option)}
                className="capitalize"
              >
                {option}
              </Button>
            ))}
          </div>

          <label className="block">
            <span className="mb-1 block text-content-secondary text-sm">
              Amount ({adjusting?.unit})
            </span>
            <input
              inputMode="decimal"
              value={quantity}
              onChange={(event) => setQuantity(event.target.value)}
              className="h-12 w-full rounded-lg border border-surface-border bg-surface-raised px-3 text-content"
            />
          </label>

          <label className="block">
            <span className="mb-1 block text-content-secondary text-sm">
              Reason — this goes in the ledger
            </span>
            <input
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder="Weekly delivery, spoiled overnight…"
              className="h-12 w-full rounded-lg border border-surface-border bg-surface-raised px-3 text-content"
            />
          </label>
        </div>
      </Modal>
    </div>
  )
}
